import React, { createContext, useContext, useState, useEffect, useRef } from 'react';

const UpdateContext = createContext(null);

const isElectron = typeof window !== 'undefined' && !!window.appUpdater;

export function UpdateProvider({ children }) {
  const [state,   setState]   = useState(null);
  const [version, setVersion] = useState('');
  const [percent, setPercent] = useState(0);
  const [message, setMessage] = useState('');
  const [open,    setOpen]    = useState(false);
  // The INSTALLED version, as opposed to `version` above, which is the one
  // being offered and stays empty while the app is up to date. Fetched once
  // here so the sidebar button and the modal share a single lookup.
  const [appVersion, setAppVersion] = useState('');
  const timerRef = useRef(null);

  const clearTimer = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => {
    if (!isElectron || !window.electronEnv?.systemInfo) return;
    window.electronEnv.systemInfo()
      .then((info) => setAppVersion(info?.app || ''))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isElectron) return;

    window.appUpdater.onStatus((data) => {
      clearTimer();
      const manual = !!data.manual;

      switch (data.event) {
        case 'checking':
          setState('checking');
          if (manual) setOpen(true);
          break;

        case 'available':
          // Downloads silently — close any open modal (e.g. from checking), no new one.
          // The main process starts the download itself (autoUpdater.autoDownload),
          // so calling download() here would kick off a second, racing transfer.
          setVersion(data.version || '');
          setState('available');
          setOpen(false);
          break;

        case 'not-available':
          setState('not-available');
          if (manual) {
            setOpen(true);
            timerRef.current = setTimeout(() => setOpen(false), 4000);
          }
          break;

        case 'downloading':
          // Background download — update percent only, don't touch open state
          // (user may have manually opened the modal via sidebar; don't close it)
          setPercent(data.percent || 0);
          setState('downloading');
          break;

        case 'downloaded':
          // Download complete — surface modal to prompt restart
          setVersion(data.version || '');
          setState('downloaded');
          setOpen(true);
          break;

        case 'error':
          setMessage(data.message || 'Update check failed.');
          setState('error');
          if (manual) setOpen(true);
          break;

        default:
          break;
      }
    });

    return () => { window.appUpdater.offStatus(); clearTimer(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <UpdateContext.Provider value={{ state, version, percent, message, open, setOpen, appVersion }}>
      {children}
    </UpdateContext.Provider>
  );
}

export const useUpdate = () => useContext(UpdateContext);
